// GPS tracking — @capacitor-community/background-geolocation (foreground service).
//
// Funguje i se zamčenou obrazovkou — plugin drží foreground service s notifikací.
// - Pri startu smeny -> start tracking (addWatcher)
// - Pri end smeny -> stop tracking (removeWatcher)
// - Vsechny pozice -> Dexie pendingGps queue -> syncNow() ihned po každém bodu

import { create } from "zustand";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { queueGps } from "../lib/db";
import { useSyncStore } from "./syncStore";

const BackgroundGeolocation = registerPlugin<any>("BackgroundGeolocation");

const SAMPLE_INTERVAL_MS = 60_000;

// distanceFilter = 0 → plugin hlásí každý fix z OS, i když auto stojí. Dřív tu bylo
// 10 m, což znamenalo, že stojící řidič nevyslal NIC a poloha v dispečinku stárla;
// zálohou měl být JS `setInterval`, jenže ten Android na pozadí uspí, takže právě
// v tu chvíli netikal. Teď tedy tempo neřídí filtr vzdálenosti, ale throttle
// `SAMPLE_INTERVAL_MS` níž — callback je nativní a běží i na pozadí.
// Cena: GPS drží fix nepřetržitě. Foreground service ho stejně drží, takže rozdíl
// v baterii je malý; jistota polohy má u svozu hotovosti přednost.
const MIN_DISTANCE_M = 0;

function uuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}


interface GpsState {
  tracking: boolean;
  shiftId: string | null;
  driverId: string | null;
  permission: "unknown" | "granted" | "denied" | "unsupported" | "error";
  lastPosition: { lat: number; lng: number; ts: string } | null;

  /** Pozada o permission a pak spusti watchPosition. */
  start: (shiftId: string, driverId: string) => Promise<void>;

  /** Zastavi tracking — vola se pri end shift. */
  stop: () => Promise<void>;
}

let _watchId: string | null = null;
let _lastSampleAt = 0;

export const useGpsStore = create<GpsState>((set) => ({
  tracking: false,
  shiftId: null,
  driverId: null,
  permission: "unknown",
  lastPosition: null,

  start: async (shiftId, driverId) => {
    // POZOR: `tracking` se NEsmí nastavit dopředu. Dřív se zapínalo hned tady a
    // shazovalo jen při odmítnutém oprávnění — takže když spadl `addWatcher` nebo
    // appka běžela v prohlížeči, svítilo řidiči zeleně „GPS aktivní", i když se
    // nesbíralo vůbec nic. Zapneme ho až když watcher reálně naskočí.
    set({ shiftId, driverId });

    // PWA/prohlížeč nemá foreground service → poloha na pozadí je technicky
    // nemožná. Radši to řekneme rovnou, než abychom předstírali funkční tracking.
    if (!Capacitor.isNativePlatform()) {
      set({ tracking: false, permission: "unsupported" });
      return;
    }
    try {
      // Android 14+: foreground service type=location vyžaduje že location permission
      // je granted PŘED voláním startForeground(). Plugin to nestíhá sám — musíme požádat
      // explicitně a počkat na výsledek, jinak SecurityException a service nenaběhne.
      const perms = await Geolocation.requestPermissions({ permissions: ["location"] });
      if (perms.location === "denied") {
        set({ permission: "denied", tracking: false });
        return;
      }
    } catch {
      // requestPermissions může házet — addWatcher si oprávnění vyřeší sám
    }
    try {
      _watchId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: "Routera sleduje tvou polohu při aktivní směně.",
          backgroundTitle: "Routera Driver",
          requestPermissions: true,
          stale: false,
          distanceFilter: MIN_DISTANCE_M,
        },
        (location: any, error: any) => {
          if (error || !location) {
            if (error?.code === "NOT_AUTHORIZED") {
              set({ permission: "denied" });
            }
            return;
          }
          set({ permission: "granted" });

          const now = Date.now();
          if (now - _lastSampleAt < SAMPLE_INTERVAL_MS) return;
          _lastSampleAt = now;

          const newPos = { lat: location.latitude, lng: location.longitude };
          void persistPos(shiftId, driverId, location);
          set({ lastPosition: { ...newPos, ts: new Date(location.time).toISOString() } });
        },
      );
      // Watcher běží — teprve teď smí indikátor svítit zeleně.
      set({ tracking: true });
    } catch (e) {
      console.warn("[gps] addWatcher failed:", e);
      set({ tracking: false, permission: "error" });
      return;
    }

    // Žádný JS `setInterval` — tempo drží throttle přímo v nativním callbacku výš.
  },

  stop: async () => {
    if (_watchId) {
      try { await BackgroundGeolocation.removeWatcher({ id: _watchId }); } catch { /* ignore */ }
      _watchId = null;
    }
    _lastSampleAt = 0;
    set({ tracking: false, shiftId: null, driverId: null, lastPosition: null });
  },
}));

async function persistPos(shiftId: string, driverId: string, pos: any): Promise<void> {
  try {
    await queueGps({
      id: uuid(),
      shift_id: shiftId,
      driver_id: driverId,
      ts: new Date(pos.time).toISOString(),
      lat: pos.latitude,
      lng: pos.longitude,
      accuracy: pos.accuracy ?? null,
      battery: null,
    });
    // Odešli HNED z tohohle (nativního) GPS callbacku. Ten běží i na POZADÍ
    // (foreground service), narozdíl od JS setInterval v syncStore, který Android
    // na pozadí uspí → jinak se poloha do dispečinku odešle až po nahození appky.
    // persistPos běží max ~1/min (SAMPLE_INTERVAL), takže to nespamuje; syncNow má
    // vlastní `syncing` guard proti překryvu a `online` check.
    void useSyncStore.getState().refreshCount();
    void useSyncStore.getState().syncNow();
  } catch (e) {
    console.error("[gps] queueGps failed:", e);
  }
}
